import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Globe, Shield, Zap, Users, ArrowRight } from 'lucide-react';
import Footer from './Footer';
import SEO from './SEO';

const About = () => {
    return (
        <div className="min-h-screen bg-soft-blue">
            <SEO
                title="Hakkımızda — CeviriPro"
                description="CeviriPro, dil bariyerlerini ortadan kaldırmak için yapay zeka ve OCR teknolojisi ile PDF, DOCX ve EPUB belgelerini çeviren Türk girişimi."
                canonical="/hakkimizda"
            />

            {/* Nav Bar */}
            <nav className="glass sticky top-0 z-50 border-b border-blue-100/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-14">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="bg-gradient-to-r from-blue-400 to-indigo-400 p-1.5 rounded-lg">
                                <Globe className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-lg font-bold text-gradient-blue">ÇeviriPro</span>
                        </Link>
                        <div className="flex items-center gap-3">
                            <Link to="/login" className="text-slate-600 hover:text-blue-500 text-sm">Giriş</Link>
                            <Link to="/register" className="px-3 py-1.5 bg-gradient-to-r from-blue-400 to-indigo-400 text-white text-sm rounded-lg hover:shadow-md transition-all">
                                Ücretsiz Başla
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <span className="inline-block px-4 py-1.5 bg-white border border-blue-100 rounded-full text-blue-500 text-sm font-medium mb-6">
                        Hakkımızda
                    </span>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 mb-5 leading-tight">
                        Dil Bariyerlerini <br />
                        <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-cyan-400 bg-clip-text text-transparent">
                            Ortadan Kaldırıyoruz
                        </span>
                    </h1>
                    <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                        CeviriPro, PDF, Word ve EPUB dosyalarınızı yapay zeka destekli teknoloji ile hızlı ve doğru biçimde çeviren bir platform.
                    </p>
                </div>
            </section>

            {/* Mission */}
            <section className="py-12 px-4">
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">Misyonumuz</h2>
                        <p className="text-slate-600 text-base leading-relaxed mb-4">
                            Dünya genelinde milyonlarca insan, dil engeli yüzünden değerli bilgiye erişemiyor. Biz bu engeli kaldırmak için çalışıyoruz.
                        </p>
                        <p className="text-slate-600 text-base leading-relaxed">
                            Araştırmacılardan öğrencilere, işletmelere kadar herkesin dokümanlarını kolayca çevirebildiği bir dünya hayal ediyoruz.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {[
                            { icon: Globe, title: "100+", sub: "Dil" },
                            { icon: Shield, title: "%100", sub: "Güvenli" },
                            { icon: Zap, title: "Saniyeler", sub: "Hızlı" },
                            { icon: Users, title: "7/24", sub: "Destek" },
                        ].map(({ icon: Icon, title, sub }, i) => (
                            <div key={i} className="card-soft p-4 rounded-xl text-center">
                                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-2">
                                    <Icon className="w-5 h-5 text-blue-500" />
                                </div>
                                <div className="text-xl font-bold text-gradient-blue">{title}</div>
                                <div className="text-xs text-slate-500">{sub}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="py-12 px-4 bg-white/50">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-10">
                        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Değerlerimiz</h2>
                        <p className="text-slate-500">Her kararımızın arkasında bunlar var.</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                        {[
                            { icon: Shield, title: "Gizlilik Önce", desc: "Belgeleriniz sunucularımızda yalnızca işlem süresince tutulur ve silinir." },
                            { icon: Zap, title: "Hız & Kalite", desc: "En gelişmiş çeviri motorunu kullanarak hem hızlı hem doğal çeviriler üretiyoruz." },
                            { icon: Heart, title: "Kullanıcı Odaklı", desc: "Her özelliği gerçek kullanıcı ihtiyaçlarına göre tasarlıyoruz." },
                        ].map(({ icon: Icon, title, desc }, i) => (
                            <div key={i} className="card-soft p-5 rounded-xl">
                                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                                    <Icon className="w-6 h-6 text-blue-500" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
                                <p className="text-slate-500 text-sm">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                        Hemen Başlayın
                    </h2>
                    <p className="text-slate-600 mb-6">
                        Binlerce kullanıcı gibi siz de ÇeviriPro'nun avantajlarından yararlanın.
                    </p>
                    <Link to="/register" className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-400 to-indigo-400 text-white font-medium rounded-xl hover:shadow-lg transition-all">
                        Hemen Başla <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default About;
